import React, { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth, ThemeProvider } from './contexts'
import { ProtectedRoute, AdminRoute } from './components'
import { LoginLayout, AppLayout } from './layouts'
import { LoadingSpinner } from './components/ui'
import './App.css'

// Lazy load pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const QuotesPage = lazy(() => import('./pages/quotes/QuotesPage').then(m => ({ default: m.QuotesPage })))
const CreateQuotePage = lazy(() => import('./pages/quotes/CreateQuotePage').then(m => ({ default: m.CreateQuotePage })))
const EditQuotePage = lazy(() => import('./pages/quotes/EditQuotePage').then(m => ({ default: m.EditQuotePage })))
const CreateAdHocQuotePage = lazy(() => import('./pages/quotes/CreateAdHocQuotePage').then(m => ({ default: m.CreateAdHocQuotePage })))
const EditAdHocQuotePage = lazy(() => import('./pages/quotes/EditAdHocQuotePage').then(m => ({ default: m.EditAdHocQuotePage })))
const UserProfilePage = lazy(() => import('./pages/profile/UserProfilePage').then(m => ({ default: m.UserProfilePage })))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const RoadmapPage = lazy(() => import('./pages/roadmap/RoadmapPage').then(m => ({ default: m.RoadmapPage })))
const LinksPage = lazy(() => import('./pages/LinksPage').then(m => ({ default: m.LinksPage })))

// Admin Pages
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage').then(m => ({ default: m.UserManagementPage })))
// const ApiTestPage = lazy(() => import('./pages/admin/ApiTestPage').then(m => ({ default: m.ApiTestPage })))
const ServiceLineManagementPage = lazy(() => import('./pages/admin/ServiceLineManagementPage').then(m => ({ default: m.ServiceLineManagementPage })))
const ServiceLineItemManagementPage = lazy(() => import('./pages/admin/ServiceLineItemManagementPage').then(m => ({ default: m.ServiceLineItemManagementPage })))
const BillingArrangementManagementPage = lazy(() => import('./pages/admin/BillingArrangementManagementPage').then(m => ({ default: m.BillingArrangementManagementPage })))
const BillingFrequencyManagementPage = lazy(() => import('./pages/admin/BillingFrequencyManagementPage').then(m => ({ default: m.BillingFrequencyManagementPage })))
const PriceManagementPage = lazy(() => import('./pages/admin/PriceManagementPage').then(m => ({ default: m.PriceManagementPage })))
const ServiceLineItemPricingRuleManagementPage = lazy(() => import('./pages/admin/ServiceLineItemPricingRuleManagementPage').then(m => ({ default: m.ServiceLineItemPricingRuleManagementPage })))
const AppSettingsManagementPage = lazy(() => import('./pages/admin/AppSettingsManagementPage').then(m => ({ default: m.AppSettingsManagementPage })))
const TemplateManagementPage = lazy(() => import('./pages/admin/TemplateManagementPage').then(m => ({ default: m.TemplateManagementPage })))
const BillingEntityManagementPage = lazy(() => import('./pages/admin/BillingEntityManagementPage').then(m => ({ default: m.BillingEntityManagementPage })))
const BillingEntityReportPage = lazy(() => import('./pages/admin/BillingEntityReportPage').then(m => ({ default: m.BillingEntityReportPage })))
const PricingDocumentationPage = lazy(() => import('./pages/admin/PricingDocumentationPage').then(m => ({ default: m.PricingDocumentationPage })))
const DocumentationPage = lazy(() => import('./pages/admin/DocumentationPage')) // Default export
const ServiceLineDocumentationPage = lazy(() => import('./pages/admin/ServiceLineDocumentationPage')) // Default export
const DocumentationIndexPage = lazy(() => import('./pages/admin/DocumentationIndexPage')) // Default export
const DocumentContactsPage = lazy(() => import('./pages/admin/DocumentContactsPage').then(m => ({ default: m.DocumentContactsPage })))
const StaticVariablePage = lazy(() => import('./pages/admin/StaticVariablePage').then(m => ({ default: m.StaticVariablePage })))
const LinkManagementPage = lazy(() => import('./pages/admin/LinkManagementPage').then(m => ({ default: m.LinkManagementPage })))

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/quotes/create" replace />
              ) : (
                <LoginLayout>
                  <LoginPage />
                </LoginLayout>
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DashboardPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <QuotesPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/create"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CreateQuotePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/edit/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <EditQuotePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/ad-hoc"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CreateAdHocQuotePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/ad-hoc/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <EditAdHocQuotePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UserProfilePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <AdminRoute>
                <AppLayout>
                  <ReportsPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/roadmap"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <RoadmapPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/links"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <LinksPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AppLayout>
                  <UserManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          {/* <Route
            path="/admin/api-test"
            element={
              <AdminRoute>
                <AppLayout>
                  <ApiTestPage />
                </AppLayout>
              </AdminRoute>
            }
          /> */}
          <Route
            path="/admin/service-lines"
            element={
              <AdminRoute>
                <AppLayout>
                  <ServiceLineManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/service-line-items"
            element={
              <AdminRoute>
                <AppLayout>
                  <ServiceLineItemManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing-arrangements"
            element={
              <AdminRoute>
                <AppLayout>
                  <BillingArrangementManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing-frequencies"
            element={
              <AdminRoute>
                <AppLayout>
                  <BillingFrequencyManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/pricing-rules"
            element={
              <AdminRoute>
                <AppLayout>
                  <PriceManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/service-line-item-pricing-rules"
            element={
              <AdminRoute>
                <AppLayout>
                  <ServiceLineItemPricingRuleManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/templates"
            element={
              <AdminRoute>
                <AppLayout>
                  <TemplateManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/documentation"
            element={
              <AdminRoute>
                <AppLayout>
                  <DocumentationIndexPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/documentation/template-guide"
            element={
              <AdminRoute>
                <AppLayout>
                  <DocumentationPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/service-line-documentation"
            element={
              <AdminRoute>
                <AppLayout>
                  <ServiceLineDocumentationPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/documentation/pricing-guide"
            element={
              <AdminRoute>
                <AppLayout>
                  <PricingDocumentationPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing-entities"
            element={
              <AdminRoute>
                <AppLayout>
                  <BillingEntityManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/billing-entity-report"
            element={
              <AdminRoute>
                <AppLayout>
                  <BillingEntityReportPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/app-settings"
            element={
              <AdminRoute>
                <AppLayout>
                  <AppSettingsManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/document-contacts"
            element={
              <AdminRoute>
                <AppLayout>
                  <DocumentContactsPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/static-variables"
            element={
              <AdminRoute>
                <AppLayout>
                  <StaticVariablePage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/links"
            element={
              <AdminRoute>
                <AppLayout>
                  <LinkManagementPage />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/"
            element={<Navigate to="/quotes/create" replace />}
          />
          <Route
            path="*"
            element={
              <div className="not-found">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
                <Navigate to="/quotes/create" replace />
              </div>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  )
}

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
